import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Message } from '@/stores/messageStore'
import { Bot, Loader2, User, RefreshCw, AlertCircle, Copy, Check, Coins, StopCircle, Sparkles, Palette, Info, RotateCcw, ArrowUpDown, FileText, ChevronDown, ChevronRight, LayoutList, Edit3, Plus, Trash2, Play, Square, Mic } from 'lucide-react'
import { MarkdownPreview } from './MarkdownPreview'
import { UnifiedThinkingIndicator } from './UnifiedThinkingIndicator'
import { ToolCallPanel, ToolCall } from './ToolCallPanel'
import { ColorAwareText } from './ColorAwareText'
import { RetryStatusIndicator } from './RetryStatusIndicator'
import { NarrativeContextDisplay, type NarrativeContext } from './NarrativeContextDisplay'
import { parseOutlineSummary, parseOutlineResult, parseOutlineError, OutlineResultDisplay, OutlineErrorDisplay } from './OutlineResultDisplay'
import { useState, useEffect, useRef } from 'react'
import { useVoiceCatalog, voiceLine } from '@/hooks/useVoiceCatalog'

// AI Edit Queue item type for compact card display
interface AIEditQueueItem {
  id: string
  elementName: string
  instruction: string
}

// Outline Change Queue item type for compact card display
interface OutlineChangeQueueDisplayItem {
  id: string
  slideId: string
  slideTitle: string
  changeType: string
  description: string
}

// Helper to detect and parse AI edit messages
const parseAIEditMessage = (content: string): { isAIEdit: boolean; queue?: AIEditQueueItem[] } => {
  const match = content.match(/\[AI_EDIT_QUEUE\](.*?)\[\/AI_EDIT_QUEUE\]/s)
  if (match) {
    try {
      const queue = JSON.parse(match[1]) as AIEditQueueItem[]
      return { isAIEdit: true, queue }
    } catch {
      return { isAIEdit: false }
    }
  }
  return { isAIEdit: false }
}

// Helper to detect and parse outline change messages
const parseOutlineChangeMessage = (content: string): { isOutlineChange: boolean; queue?: OutlineChangeQueueDisplayItem[]; remainingContent?: string } => {
  const match = content.match(/\[OUTLINE_CHANGE_QUEUE\](.*?)\[\/OUTLINE_CHANGE_QUEUE\]/s)
  if (match) {
    try {
      const queue = JSON.parse(match[1]) as OutlineChangeQueueDisplayItem[]
      // Strip the queue marker AND the AI instruction block that follows it
      const remainingContent = content
        .replace(/\[OUTLINE_CHANGE_QUEUE\].*?\[\/OUTLINE_CHANGE_QUEUE\]\s*/s, '')
        .replace(/## Outline Update Request[\s\S]*?\*\*Apply these changes to the outline and return the updated plan\.\*\*\s*/s, '')
        .trim()
      return { isOutlineChange: true, queue, remainingContent: remainingContent || undefined }
    } catch {
      return { isOutlineChange: false }
    }
  }
  return { isOutlineChange: false }
}

// Theme palette type for message display
interface ThemePaletteData {
  id: string
  name: string
  colors: {
    primary: string
    secondary: string
    accent: string
    background: string
    text: string
  }
}

// Script update type for narration messages
interface ScriptUpdate {
  slideId: string
  scriptText: string
  reason?: string
}

// Helper to detect and parse script update messages
const parseScriptUpdates = (content: string): { hasScriptUpdates: boolean; updates?: ScriptUpdate[]; remainingContent?: string } => {
  const match = content.match(/<script_updates>([\s\S]*?)<\/script_updates>/)
  if (match) {
    try {
      const updates = JSON.parse(match[1]) as ScriptUpdate[]
      // Get content before and after the script_updates block
      const remainingContent = content
        .replace(/<script_updates>[\s\S]*?<\/script_updates>\s*/g, '')
        .trim()
      return { hasScriptUpdates: true, updates, remainingContent: remainingContent || undefined }
    } catch {
      return { hasScriptUpdates: false }
    }
  }
  return { hasScriptUpdates: false }
}

// Helper to detect and parse narrative context messages
const parseNarrativeContext = (content: string): {
  hasNarrativeContext: boolean;
  context?: NarrativeContext;
  remainingContent?: string
} => {
  const match = content.match(/<narrative_context>([\s\S]*?)<\/narrative_context>/)
  if (match) {
    try {
      const context = JSON.parse(match[1]) as NarrativeContext
      // Get content before and after the narrative_context block
      const remainingContent = content
        .replace(/<narrative_context>[\s\S]*?<\/narrative_context>\s*/g, '')
        .trim()
      return { hasNarrativeContext: true, context, remainingContent: remainingContent || undefined }
    } catch {
      return { hasNarrativeContext: false }
    }
  }
  return { hasNarrativeContext: false }
}

// Helper to detect and parse voice recommendation from ```ivy code blocks
interface VoiceRecommendation {
  recommended: string
  context: string
  candidates: string[]
}

const parseVoiceRecommendation = (content: string): {
  hasVoiceRecommendation: boolean
  recommendation?: VoiceRecommendation
  remainingContent?: string
} => {
  // Match both ```ivy and ```ivy-voice-selector code blocks
  const match = content.match(/```(?:ivy-voice-selector|ivy)\n([\s\S]*?)\n```/)
  if (match) {
    try {
      const data = JSON.parse(match[1].trim())
      if (data.recommended && data.candidates && Array.isArray(data.candidates)) {
        const remainingContent = content.replace(/```(?:ivy-voice-selector|ivy)\n[\s\S]*?\n```\s*/, '').trim()
        return { hasVoiceRecommendation: true, recommendation: data, remainingContent: remainingContent || undefined }
      }
    } catch { /* not valid voice JSON */ }
  }
  return { hasVoiceRecommendation: false }
}

// ========== OUTLINE XML TYPES ==========

interface SlideOutline {
  id: string
  order: number
  title: string
  description: string
  keyPoints: string[]
  layoutId: string
  layoutName: string
}

interface VideoOutline {
  version: number
  status: string
  title: string
  templateId: string
  templateName: string
  generatedAt: string
  slides: SlideOutline[]
}

interface OutlineUpdate {
  action: 'create' | 'update' | 'delete' | 'reorder'
  slideId: string
  afterSlideId?: string
  newOrder?: number
  changes?: Partial<SlideOutline>
  slide?: SlideOutline
  reason?: string
}

interface OutlineMetadata {
  slideCount: number
  narrativeArc?: string
  validationStatus: 'passed' | 'failed' | 'pending'
  validationIssues?: string[]
}

// ========== OUTLINE XML PARSING ==========

// Parse <outline_full> - CREATE mode (full outline)
const parseOutlineFull = (content: string): {
  hasOutlineFull: boolean
  outline?: VideoOutline
  remainingContent?: string
} => {
  const match = content.match(/<outline_full>([\s\S]*?)<\/outline_full>/)
  if (match) {
    try {
      const outline = JSON.parse(match[1]) as VideoOutline
      const remainingContent = content
        .replace(/<outline_full>[\s\S]*?<\/outline_full>\s*/g, '')
        .trim()
      return { hasOutlineFull: true, outline, remainingContent: remainingContent || undefined }
    } catch {
      return { hasOutlineFull: false }
    }
  }
  return { hasOutlineFull: false }
}

// Parse <outline_updates> - EDIT mode (incremental changes)
const parseOutlineUpdates = (content: string): {
  hasOutlineUpdates: boolean
  updates?: OutlineUpdate[]
  remainingContent?: string
} => {
  const match = content.match(/<outline_updates>([\s\S]*?)<\/outline_updates>/)
  if (match) {
    try {
      const updates = JSON.parse(match[1]) as OutlineUpdate[]
      const remainingContent = content
        .replace(/<outline_updates>[\s\S]*?<\/outline_updates>\s*/g, '')
        .trim()
      return { hasOutlineUpdates: true, updates, remainingContent: remainingContent || undefined }
    } catch {
      return { hasOutlineUpdates: false }
    }
  }
  return { hasOutlineUpdates: false }
}

// Parse <outline_metadata> - both modes
const parseOutlineMetadata = (content: string): {
  hasOutlineMetadata: boolean
  metadata?: OutlineMetadata
  remainingContent?: string
} => {
  const match = content.match(/<outline_metadata>([\s\S]*?)<\/outline_metadata>/)
  if (match) {
    try {
      const metadata = JSON.parse(match[1]) as OutlineMetadata
      const remainingContent = content
        .replace(/<outline_metadata>[\s\S]*?<\/outline_metadata>\s*/g, '')
        .trim()
      return { hasOutlineMetadata: true, metadata, remainingContent: remainingContent || undefined }
    } catch {
      return { hasOutlineMetadata: false }
    }
  }
  return { hasOutlineMetadata: false }
}

// Helper to detect and parse theme messages
const parseThemeMessage = (content: string): { isTheme: boolean; theme?: ThemePaletteData; remainingContent?: string } => {
  const match = content.match(/\[THEME_PALETTE\](.*?)\[\/THEME_PALETTE\]/s)
  if (match) {
    try {
      const theme = JSON.parse(match[1]) as ThemePaletteData
      // Get content after the theme marker, and remove hidden AI instructions
      let remainingContent = content
        .replace(/\[THEME_PALETTE\].*?\[\/THEME_PALETTE\]\s*/s, '')
        .replace(/\[THEME_INSTRUCTIONS\].*?\[\/THEME_INSTRUCTIONS\]\s*/s, '')
        .trim()
      return { isTheme: true, theme, remainingContent: remainingContent || undefined }
    } catch {
      return { isTheme: false }
    }
  }
  return { isTheme: false }
}

// Compact AI Edit Message Display Component
const AIEditMessageDisplay: React.FC<{ queue: AIEditQueueItem[] }> = ({ queue }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-sm text-primary dark:text-accent-foreground">
      <Sparkles className="h-4 w-4" />
      <span className="font-medium">{queue.length} AI Edit{queue.length !== 1 ? 's' : ''} Requested</span>
    </div>
    <div className="flex flex-wrap gap-2">
      {queue.map((item) => (
        <div
          key={item.id}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent rounded-lg border border-primary/30 max-w-[280px]"
        >
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-accent-foreground truncate">
              {item.elementName}
            </div>
            <div className="text-xs text-primary dark:text-accent-foreground truncate">
              {item.instruction}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)

// Unified neutral chip style for sent outline changes (inside user bubble)
const sentChipStyle = {
  container: 'bg-white/60 dark:bg-white/[0.06] border border-white/20 dark:border-white/10 rounded-lg',
  title: 'text-foreground',
  desc: 'text-foreground/60',
}

const sentChipIcons: Record<string, React.ReactNode> = {
  layout_change: <LayoutList className="h-3 w-3 shrink-0 text-foreground/70" />,
  add_slide: <Plus className="h-3 w-3 shrink-0 text-foreground/70" />,
  delete_slide: <Trash2 className="h-3 w-3 shrink-0 text-foreground/70" />,
}

// Outline Change Message Display - chip style matching queued display (no remove button)
const OutlineChangeMessageDisplay: React.FC<{ queue: OutlineChangeQueueDisplayItem[] }> = ({ queue }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-1.5 text-xs text-foreground">
      <LayoutList className="h-3.5 w-3.5" />
      <span className="font-medium">{queue.length} Outline Change{queue.length !== 1 ? 's' : ''}</span>
    </div>
    <div className="flex flex-col gap-1.5">
      {queue.map((item) => {
        const icon = sentChipIcons[item.changeType] || sentChipIcons.layout_change
        return (
          <div
            key={item.id}
            className={`flex items-start gap-2 px-3 py-1.5 ${sentChipStyle.container}`}
          >
            <div className="shrink-0 mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-medium ${sentChipStyle.title}`}>
                {item.slideTitle}
              </div>
              <div className={`text-xs ${sentChipStyle.desc}`}>
                {item.description}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  </div>
)

// Theme Palette Message Display Component (compact chip style)
const ThemeMessageDisplay: React.FC<{ theme: ThemePaletteData }> = ({ theme }) => (
  <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-accent rounded-lg border border-primary/30">
    <Palette className="h-3.5 w-3.5 text-primary" />

    {/* Color dots */}
    <div className="flex gap-0.5">
      {Object.values(theme.colors).map((color, i) => (
        <div
          key={i}
          className="w-4 h-4 rounded-full border border-white/40 ring-1 ring-black/5"
          style={{ backgroundColor: color }}
        />
      ))}
    </div>

    {/* Theme name */}
    <span className="text-xs font-medium text-accent-foreground">
      {theme.name}
    </span>
  </div>
)

// Script Updates Display Component (collapsible)
const ScriptUpdatesDisplay: React.FC<{ updates: ScriptUpdate[] }> = ({ updates }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="mt-3 border border-emerald-200 dark:border-emerald-800 rounded-lg overflow-hidden">
      {/* Collapsed header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        )}
        <FileText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
          {updates.length} Script{updates.length !== 1 ? 's' : ''} Updated
        </span>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 py-2 space-y-3 bg-white dark:bg-gray-900 max-h-[400px] overflow-y-auto">
          {updates.map((update, index) => (
            <div
              key={update.slideId || index}
              className="border-l-2 border-emerald-400 pl-3 py-1"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono bg-emerald-100 dark:bg-emerald-900/50 px-1.5 py-0.5 rounded text-emerald-700 dark:text-emerald-300">
                  {update.slideId}
                </span>
                {update.reason && (
                  <span className="text-xs text-muted-foreground italic">
                    — {update.reason}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {update.scriptText}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ========== VOICE RECOMMENDATION COMPONENT ==========

const VoiceRecommendationCard: React.FC<{ recommendation: VoiceRecommendation }> = ({ recommendation }) => {
  const [selected, setSelected] = useState(recommendation.recommended)
  const [playing, setPlaying] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const catalog = useVoiceCatalog()

  const handlePlay = (name: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (playing === name) {
      audioRef.current?.pause()
      audioRef.current = null
      setPlaying(null)
      return
    }
    const sampleUrl = catalog?.get(name)?.sampleUrl
    if (!sampleUrl) return
    audioRef.current?.pause()
    const audio = new Audio(sampleUrl)
    audioRef.current = audio
    audio.addEventListener('ended', () => setPlaying(null))
    audio.addEventListener('error', () => setPlaying(null))
    audio.play().catch(() => setPlaying(null))
    setPlaying(name)
  }

  useEffect(() => {
    return () => { audioRef.current?.pause() }
  }, [])

  const handleConfirm = () => {
    setSubmitted(true)
    window.dispatchEvent(new CustomEvent('ivy-voice-selected', { detail: { voice: selected } }))
  }

  return (
    <div className="mt-3 rounded-lg border border-primary/30 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-accent border-b border-primary/30">
        <Mic className="h-4 w-4 text-primary dark:text-accent-foreground" />
        <span className="text-sm font-medium text-accent-foreground">Pick a voice</span>
        <span className="text-xs text-muted-foreground ml-auto">{recommendation.context}</span>
      </div>
      <div className="p-2 space-y-1">
        {recommendation.candidates.map((name) => {
          const meta = catalog?.get(name)
          const isRecommended = name === recommendation.recommended
          const isSelected = name === selected
          const isPlaying = playing === name

          return (
            <button
              key={name}
              disabled={submitted}
              onClick={() => setSelected(name)}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-left',
                'disabled:opacity-60 disabled:cursor-default',
                isSelected
                  ? 'bg-accent border border-primary/30'
                  : 'hover:bg-muted/50 border border-transparent'
              )}
            >
              <button
                onClick={(e) => handlePlay(name, e)}
                disabled={submitted}
                className={cn(
                  'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors',
                  isPlaying
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted-foreground/20'
                )}
              >
                {isPlaying ? (
                  <Square className="h-2.5 w-2.5 fill-current" />
                ) : (
                  <Play className="h-2.5 w-2.5 fill-current ml-0.5" />
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={cn('text-sm font-medium', isSelected && 'text-primary')}>
                    {name}
                  </span>
                  {meta && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {meta.gender === 'female' ? '♀' : '♂'}
                    </span>
                  )}
                  {isRecommended && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium">
                      Recommended
                    </span>
                  )}
                </div>
                {meta && (
                  <span className="text-xs text-muted-foreground">{voiceLine(meta)}</span>
                )}
              </div>

              <div className={cn(
                'w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors',
                isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'
              )}>
                {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
            </button>
          )
        })}
      </div>
      <div className="px-3 pb-3 pt-1">
        <button
          onClick={handleConfirm}
          disabled={submitted}
          className={cn(
            'w-full py-2 rounded-lg text-sm font-medium transition-all',
            submitted
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 cursor-default'
              : 'bg-primary hover:bg-primary/90 text-primary-foreground'
          )}
        >
          {submitted ? `✓ Using ${selected}` : `Use ${selected}`}
        </button>
      </div>
    </div>
  )
}

// ========== OUTLINE DISPLAY COMPONENTS ==========

// Outline Full Display - Clean inline chip showing outline was created
const OutlineFullDisplay: React.FC<{ outline: VideoOutline }> = ({ outline }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="mt-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-muted border border-border rounded-full hover:border-foreground/20 transition-all text-xs group"
      >
        <LayoutList className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-medium text-foreground">Outline</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{outline.slides.length} slides</span>
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground ml-0.5" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground ml-0.5 group-hover:translate-x-0.5 transition-transform" />
        )}
      </button>

      {/* Expanded slide list */}
      {isExpanded && (
        <div className="mt-2 ml-1 pl-3 border-l-2 border-border space-y-1.5 max-h-[300px] overflow-y-auto">
          {outline.slides.map((slide, index) => (
            <div key={slide.id || index} className="flex items-baseline gap-2 text-xs">
              <span className="font-mono text-muted-foreground shrink-0">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="text-gray-700 dark:text-gray-300 truncate">
                {slide.title}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Outline Updates Display - Clean inline showing what changed
const OutlineUpdatesDisplay: React.FC<{ updates: OutlineUpdate[] }> = ({ updates }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Summarize actions
  const actionCounts = updates.reduce((acc, u) => {
    acc[u.action] = (acc[u.action] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const summary = Object.entries(actionCounts)
    .map(([action, count]) => `${count} ${action}${count > 1 ? 'd' : ''}`)
    .join(', ')

  return (
    <div className="mt-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-muted border border-border rounded-full hover:border-foreground/20 transition-all text-xs group"
      >
        <Edit3 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-medium text-foreground">Updated</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{summary}</span>
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground ml-0.5" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground ml-0.5 group-hover:translate-x-0.5 transition-transform" />
        )}
      </button>

      {/* Expanded changes list */}
      {isExpanded && (
        <div className="mt-2 ml-1 pl-3 border-l-2 border-border space-y-1 max-h-[200px] overflow-y-auto">
          {updates.map((update, index) => (
            <div key={`${update.slideId}-${index}`} className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 rounded font-medium shrink-0 bg-muted/80 text-muted-foreground">
                {update.action}
              </span>
              <span className="font-mono text-gray-500 dark:text-gray-400">
                {update.slideId}
              </span>
              {update.action === 'create' && update.slide && (
                <span className="text-gray-600 dark:text-gray-300 truncate">
                  {update.slide.title}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Simplified - we don't render metadata separately anymore

interface MessageBubbleProps {
  message: Message
  onRetry?: (conversationId: string, originalUserMessage: string) => void
  previousUserMessage?: string
}

export function MessageBubble({
  message,
  onRetry,
  previousUserMessage
}: MessageBubbleProps) {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'
  const isVersionRestore = isSystem && (message.metadata as any)?.type === 'version_restore'
  const isSlideReorder = isSystem && (message.metadata as any)?.type === 'slide_reorder'
  const isStreaming = message.status === 'streaming'
  const hasError = message.status === 'error'
  const [copied, setCopied] = useState(false)

  // Track if we've dispatched the narrative context event to avoid duplicates
  const narrativeContextDispatchedRef = useRef<string | null>(null)

  // Parse narrative context for event dispatch (only for assistant messages)
  const narrativeDataForEvent = !isUser && !isSystem ? parseNarrativeContext(message.content) : { hasNarrativeContext: false }

  // Dispatch narrative-context-updated event when context is parsed
  // This allows NarrationView to update its cache in real-time
  useEffect(() => {
    if (
      narrativeDataForEvent.hasNarrativeContext &&
      narrativeDataForEvent.context &&
      message.conversationId &&
      !isStreaming && // Only dispatch when message is complete, not during streaming
      narrativeContextDispatchedRef.current !== message.id // Prevent duplicate dispatches
    ) {
      console.log('[MessageBubble] Dispatching narrative-context-updated event', {
        conversationId: message.conversationId,
        messageId: message.id,
      })
      narrativeContextDispatchedRef.current = message.id
      window.dispatchEvent(
        new CustomEvent('narrative-context-updated', {
          detail: {
            conversationId: message.conversationId,
            context: narrativeDataForEvent.context,
          },
        })
      )
    }
  }, [
    narrativeDataForEvent.hasNarrativeContext,
    narrativeDataForEvent.context,
    message.conversationId,
    message.id,
    isStreaming,
  ])

  // Retry the previous user message
  const handleRetry = async () => {
    if (!isUser && hasError && previousUserMessage && onRetry) {
      onRetry(message.conversationId, previousUserMessage)
    }
  }

  // Copy message content to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
  
  const formatTime = (date: Date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Format time portion
    const timeStr = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Check if same day
    const isToday = messageDate.toDateString() === now.toDateString();

    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = messageDate.toDateString() === yesterday.toDateString();

    // Check if this week (within 7 days and same year)
    const isThisWeek = diffDays < 7 && messageDate.getFullYear() === now.getFullYear();

    // Smart formatting based on recency
    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60 && isToday) {
      return `${diffMins}m ago`;
    } else if (diffHours < 6 && isToday) {
      return `${diffHours}h ago`;
    } else if (isToday) {
      return timeStr;
    } else if (isYesterday) {
      return `Yesterday ${timeStr}`;
    } else if (isThisWeek) {
      const dayName = messageDate.toLocaleDateString([], { weekday: 'short' });
      return `${dayName} ${timeStr}`;
    } else if (messageDate.getFullYear() === now.getFullYear()) {
      // Same year: "Jan 15, 10:30 AM"
      const dateStr = messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
      return `${dateStr}, ${timeStr}`;
    } else {
      // Different year: "Jan 15, 2024"
      return messageDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
  

  const formatContent = (content: string) => {
    // Use MarkdownPreview for messages that contain markdown
    if (content.includes('##') ||
      content.includes('**') ||
      content.includes('```') ||
      content.includes('*') ||
      content.includes('`') ||
      content.includes('-') ||
      content.includes('![') ||
      content.includes('<audio') ||
      content.includes('<video') ||
      content.includes('<iframe')
    ) {
      return <MarkdownPreview content={content} className="text-sm" />
    }
    
    // For user messages or simple text, use the original formatting
    return content.split('```').map((part, index) => {
      if (index % 2 === 1) {
        // Code blocks
        return (
          <pre key={index} className="bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs overflow-x-auto max-w-full my-2 ml-auto">
            <code className="block max-w-full">{part}</code>
          </pre>
        )
      }
      // Regular text with color detection
      return (
        <div key={index} className="whitespace-pre-wrap text-wrap-anywhere">
          <ColorAwareText text={part} />
        </div>
      )
    })
  }


  return (
    <div className="w-full">
      {/* System Message - Different styles for different types */}
      {isSystem ? (
        isSlideReorder ? (
          /* Slide Reorder - Compact inline chip (black & white scheme) */
          <div className="mx-auto max-w-3xl px-4 flex justify-center">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-muted/50 dark:bg-zinc-800/50 border border-border dark:border-zinc-700 rounded-full text-xs">
              <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground font-medium">{message.content}</span>
              <span className="text-muted-foreground/50">·</span>
              <span className="text-muted-foreground/60">{formatTime(message.timestamp)}</span>
            </div>
          </div>
        ) : (
          /* Version Restore & Other System Messages - Full banner */
          <div className="mx-auto max-w-3xl px-4">
            <div className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg border",
              isVersionRestore
                ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                : "bg-accent border-primary/30"
            )}>
              {/* Icon */}
              <div className={cn(
                "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                isVersionRestore
                  ? "bg-amber-100 dark:bg-amber-800 text-amber-600 dark:text-amber-300"
                  : "bg-accent text-primary dark:text-accent-foreground"
              )}>
                {isVersionRestore ? (
                  <RotateCcw className="h-4 w-4" />
                ) : (
                  <Info className="h-4 w-4" />
                )}
              </div>
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className={cn(
                  "text-sm",
                  isVersionRestore
                    ? "text-amber-800 dark:text-amber-200"
                    : "text-accent-foreground"
                )}>
                  {formatContent(message.content)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
      <div className={cn(
        "mx-auto max-w-3xl px-4",
        isUser ? "flex justify-end" : "flex justify-start"
      )}>
        {isUser ? (
          /* User Message - Right aligned, compact */
          <div className={cn(
            "flex gap-3 flex-row-reverse items-start",
            message.content.includes('[OUTLINE_CHANGE_QUEUE]') ? "max-w-[85%]" : "max-w-[70%]"
          )}>
            {/* User Avatar */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-primary text-primary-foreground">
              <User className="h-4 w-4" />
            </div>
            {/* User Message Bubble */}
            <div className="flex flex-col items-end gap-1 min-w-0">
              <div className="user-message-bubble-color rounded-[18px] px-4 py-2 max-w-full min-w-0">
                <div className="whitespace-pre-wrap break-words text-sm min-w-0 max-w-full">
                  {(() => {
                    // Check if this is an AI edit message
                    const aiEditData = parseAIEditMessage(message.content)
                    if (aiEditData.isAIEdit && aiEditData.queue) {
                      return <AIEditMessageDisplay queue={aiEditData.queue} />
                    }

                    // Check if this is an outline change message (only show cards, hide raw markdown)
                    const outlineChangeData = parseOutlineChangeMessage(message.content)
                    if (outlineChangeData.isOutlineChange && outlineChangeData.queue) {
                      return (
                        <div className="space-y-2">
                          <OutlineChangeMessageDisplay queue={outlineChangeData.queue} />
                          {outlineChangeData.remainingContent && (
                            <div className="whitespace-pre-wrap text-wrap-anywhere">
                              <span>{outlineChangeData.remainingContent}</span>
                            </div>
                          )}
                        </div>
                      )
                    }

                    // Check if this is a theme message
                    const themeData = parseThemeMessage(message.content)
                    if (themeData.isTheme && themeData.theme) {
                      return (
                        <div className="space-y-3">
                          <ThemeMessageDisplay theme={themeData.theme} />
                          {themeData.remainingContent && (
                            <div className="pt-1">
                              {formatContent(themeData.remainingContent)}
                            </div>
                          )}
                        </div>
                      )
                    }

                    return formatContent(message.content)
                  })()}
                </div>
              </div>
              {/* User message metadata */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{formatTime(message.timestamp)}</span>
                {(() => {
                  const tokenCount = message.metadata?.inputTokenCount || message.metadata?.tokenCount;
                  const tokenLabel = message.metadata?.inputTokenCount ? 'input tokens' : 'tokens';
                  
                  if (tokenCount) {
                    return (
                      <span className="bg-muted/50 px-2 py-0.5 rounded-full flex items-center gap-1" 
                            title='Total tokens sent to model (including context)'>
                        <Coins className="h-3 w-3" />
                        {tokenCount.toLocaleString()} {tokenLabel}
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        ) : (
          /* Assistant Message - Left aligned, spacious */
          <div className="flex gap-3 w-full items-start">
            {/* Assistant Avatar */}
            <div className={cn(
              "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
              hasError 
                ? "bg-red-50 text-red-600" 
                : "bg-muted text-muted-foreground"
            )}>
              {isStreaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Bot className="h-4 w-4" />
              )}
            </div>
            {/* Assistant Message Content */}
            <div className="flex-1 min-w-0 max-w-[calc(100%-3rem)] space-y-2">
              {/* Assistant Name */}
              <div className="text-sm font-semibold text-foreground">
                Ivy
                {isStreaming && (
                  <span className="text-xs text-muted-foreground ml-2 animate-pulse">
                    Typing...
                  </span>
                )}
              </div>
              
              {/* Message Content */}
              <div className="text-sm leading-relaxed max-w-full">
                {hasError ? (
                  message.metadata?.interrupted ? (
                    // Interrupted message - show friendly message
                    <div className="text-gray-600">
                      <div className="flex items-center gap-2 mb-2">
                        <StopCircle className="w-4 h-4" />
                        <span className="font-medium">Response stopped</span>
                      </div>
                      <div className="text-xs mt-2 p-2 bg-gray-100/50 rounded border border-gray-200">
                        You stopped this response.
                      </div>
                      {previousUserMessage && onRetry && (
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRetry}
                            className="text-gray-600 border-gray-200 hover:bg-gray-50"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Retry
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Error message - show error details
                    <div className="text-red-600">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4" />
                        <span className="font-medium">Message failed</span>
                      </div>
                      {message.metadata?.error && (
                        <div className="text-xs mt-2 p-2 bg-red-100/50 rounded border border-red-200">
                          {typeof message.metadata.error === 'string'
                            ? message.metadata.error
                            : message.metadata.error.message || 'Unknown error'
                          }
                        </div>
                      )}
                      {previousUserMessage && onRetry && (
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRetry}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Retry
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div>
                    {(() => {
                      // Parse voice recommendation, narrative context and script updates
                      const voiceData = parseVoiceRecommendation(message.content)
                      const contentAfterVoice = voiceData.remainingContent || message.content
                      const narrativeData = parseNarrativeContext(contentAfterVoice)
                      const contentAfterNarrative = narrativeData.remainingContent || contentAfterVoice
                      const scriptData = parseScriptUpdates(contentAfterNarrative)

                      // Parse outline data (legacy formats)
                      const contentAfterScript = scriptData.remainingContent || contentAfterNarrative
                      const outlineFullData = parseOutlineFull(contentAfterScript)
                      const contentAfterFull = outlineFullData.remainingContent || contentAfterScript
                      const outlineUpdatesData = parseOutlineUpdates(contentAfterFull)
                      const contentAfterUpdates = outlineUpdatesData.remainingContent || contentAfterFull
                      const outlineMetadataData = parseOutlineMetadata(contentAfterUpdates)

                      // Parse new outline result/error format (both <outline_result> XML and <outline_summary> JSON)
                      const contentAfterMetadata = outlineMetadataData.remainingContent || contentAfterUpdates
                      const outlineSummaryData = parseOutlineSummary(contentAfterMetadata)
                      const contentAfterSummary = outlineSummaryData.remainingContent || contentAfterMetadata
                      const outlineResultData = parseOutlineResult(contentAfterSummary)
                      const contentAfterResult = outlineResultData.remainingContent || contentAfterSummary
                      const outlineErrorData = parseOutlineError(contentAfterResult)

                      // Merge: prefer outline_summary (JSON) if present, fallback to outline_result (XML)
                      const effectiveOutlineResult = outlineSummaryData.hasOutlineResult
                        ? outlineSummaryData
                        : outlineResultData

                      // Check if we have any special content (metadata is parsed but not displayed)
                      const hasSpecialContent =
                        voiceData.hasVoiceRecommendation ||
                        narrativeData.hasNarrativeContext ||
                        scriptData.hasScriptUpdates ||
                        outlineFullData.hasOutlineFull ||
                        outlineUpdatesData.hasOutlineUpdates ||
                        outlineMetadataData.hasOutlineMetadata ||
                        effectiveOutlineResult.hasOutlineResult ||
                        outlineErrorData.hasOutlineError

                      // Get the cleanest remaining content (with all tags stripped)
                      let textContent = message.content
                      if (hasSpecialContent) {
                        textContent = message.content
                          .replace(/```(?:ivy-voice-selector|ivy)\n[\s\S]*?\n```\s*/g, '')
                          .replace(/<narrative_context>[\s\S]*?<\/narrative_context>\s*/g, '')
                          .replace(/<script_updates>[\s\S]*?<\/script_updates>\s*/g, '')
                          .replace(/<outline_full>[\s\S]*?<\/outline_full>\s*/g, '')
                          .replace(/<outline_updates>[\s\S]*?<\/outline_updates>\s*/g, '')
                          .replace(/<outline_metadata>[\s\S]*?<\/outline_metadata>\s*/g, '')
                          .replace(/<outline_summary>[\s\S]*?<\/outline_summary>\s*/g, '')
                          .replace(/(?:^|\n)outline_summary\n[\s\S]*?\n\/outline_summary\s*/gm, '')
                          .replace(/<outline_result>[\s\S]*?<\/outline_result>\s*/g, '')
                          .replace(/<outline_error>[\s\S]*?<\/outline_error>\s*/g, '')
                          .replace(/(?:^|\n)outline_error\n[\s\S]*?\n\/outline_error\s*/gm, '')
                          .trim()
                      }

                      if (hasSpecialContent) {
                        return (
                          <>
                            {textContent && formatContent(textContent)}
                            {voiceData.hasVoiceRecommendation && voiceData.recommendation && (
                              <VoiceRecommendationCard recommendation={voiceData.recommendation} />
                            )}
                            {narrativeData.hasNarrativeContext && narrativeData.context && (
                              <NarrativeContextDisplay context={narrativeData.context} />
                            )}
                            {scriptData.hasScriptUpdates && scriptData.updates && (
                              <ScriptUpdatesDisplay updates={scriptData.updates} />
                            )}
                            {outlineFullData.hasOutlineFull && outlineFullData.outline && (
                              <OutlineFullDisplay outline={outlineFullData.outline} />
                            )}
                            {outlineUpdatesData.hasOutlineUpdates && outlineUpdatesData.updates && (
                              <OutlineUpdatesDisplay updates={outlineUpdatesData.updates} />
                            )}
                            {/* New outline result display - handles both <outline_summary> JSON and <outline_result> XML */}
                            {effectiveOutlineResult.hasOutlineResult && effectiveOutlineResult.result && (
                              <OutlineResultDisplay result={effectiveOutlineResult.result} />
                            )}
                            {outlineErrorData.hasOutlineError && outlineErrorData.error && (
                              <OutlineErrorDisplay error={outlineErrorData.error} />
                            )}
                            {/* Metadata is parsed but not displayed - keeping it minimal */}
                          </>
                        )
                      }
                      return formatContent(message.content)
                    })()}

                    {/* Show retry status indicator */}
                    {message.metadata?.retryStatus?.isRetrying && (
                      <RetryStatusIndicator
                        retryStatus={message.metadata.retryStatus}
                        className="mt-2"
                      />  
                    )}
                    
                    {/* Unified Thinking Indicator - shows processing, current thinking, and history */}
                    {(isStreaming || message.thinkingHistory?.length > 0) && (
                      <UnifiedThinkingIndicator
                        isActive={isStreaming}
                        currentThinkingSubject={message.currentThinkingSubject}
                        thinkingHistory={message.thinkingHistory || []}
                        className="mt-2"
                      />
                    )}

                    {/* Tool execution panel - only shows during streaming */}
                    {isStreaming && message.metadata?.toolCalls && message.metadata.toolCalls.length > 0 && (
                      <ToolCallPanel
                        toolCalls={message.metadata.toolCalls as ToolCall[]}
                        className="mt-2"
                      />
                    )}
                  </div>
                )}
              </div>
              
              {/* Actions and metadata */}
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <span>{formatTime(message.timestamp)}</span>
                {isStreaming && (
                  <div className="flex items-center gap-1">
                    <span className="animate-pulse text-primary">●</span>
                    <span>Live</span>
                  </div>
                )}
                {(() => {
                  // For user messages, show input tokens (total sent to model)
                  // For assistant messages, show output tokens (response tokens)
                  // Fallback to legacy tokenCount field for old messages
                  const tokenCount = message.role === 'user' 
                    ? (message.metadata?.inputTokenCount || message.metadata?.tokenCount)
                    : (message.metadata?.outputTokenCount || message.metadata?.tokenCount);
                  
                  // Show appropriate label based on what type of token count we have
                  const tokenLabel = message.role === 'user' 
                    ? (message.metadata?.inputTokenCount ? 'input tokens' : 'tokens')
                    : 'output tokens';
                  
                  if (tokenCount) {
                    return (
                      <span className="bg-muted/50 px-2 py-0.5 rounded-full flex items-center gap-1" 
                            title={message.role === 'user' 
                              ? 'Total tokens sent to model (including context)' 
                              : 'Tokens in model response'}>
                        <Coins className="h-3 w-3" />
                        {tokenCount.toLocaleString()} {tokenLabel}
                      </span>
                    );
                  }
                  return null;
                })()}
                {message.metadata?.includedInContext === false && (
                  <span className="text-yellow-600 text-xs">(excluded from context)</span>
                )}
                {!isStreaming && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="h-6 px-2 hover:bg-slate-200 dark:hover:bg-slate-700 -ml-2"
                    title={copied ? "Copied!" : "Copy message"}
                  >
                    {copied ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  )
}